import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Countdown Timer component that displays time remaining in HH:MM:SS format for durations over 60 minutes,
 * or MM:SS format for shorter durations
 * 
 * @param {number} initialMinutes - Initial time in minutes (fallback if startDate/timerDuration not provided)
 * @param {string} startDate - ISO string of the start date (UTC)
 * @param {number} timerDuration - Duration of the timer in minutes
 * @param {function} onTimeUp - Callback function to execute when timer reaches zero
 * @returns {JSX.Element} - Timer component
 */
function CountdownTimer({ initialMinutes, startDate, timerDuration, onTimeUp }) {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isLowTime, setIsLowTime] = useState(false);
  
  // Use refs to store values that should persist across renders but not trigger re-renders
  const initialRemainingMsRef = useRef(null);
  const startTimeRef = useRef(null);
  
  // Calculate initial remaining time only once when component mounts or when dependencies change
  useEffect(() => {
    let initialRemainingMs;
    
    // If startDate and timerDuration are provided, calculate remaining time based on those
    if (startDate && timerDuration) {
      // Parse start date (UTC)
      const startTimeUTC = new Date(startDate).getTime();
      
      // Get current time in UTC with full precision
      const now = new Date();
      const nowTimeUTC = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds(),
        now.getUTCMilliseconds()
      );
      
      // Calculate elapsed time since start
      const elapsedMs = nowTimeUTC - startTimeUTC;
      
      // Convert timer duration from minutes to milliseconds
      const totalTimerMs = timerDuration * 60 * 1000;
      
      // Calculate remaining time in milliseconds
      initialRemainingMs = Math.max(0, totalTimerMs - elapsedMs);      
    } 
    // Otherwise, fall back to initialMinutes
    else if (initialMinutes) {
      const minutes = Number(initialMinutes);
      // Convert minutes to milliseconds
      initialRemainingMs = minutes * 60 * 1000;
    } 
    // If neither is provided, return early
    else {
      return;
    }
    
    // Store the initial remaining time in a ref so it doesn't change on re-renders
    initialRemainingMsRef.current = initialRemainingMs;
    
    // Store the start time of this effect
    startTimeRef.current = Date.now();
    
    // Set initial time remaining
    setTimeRemaining(initialRemainingMs);
    
    // Set up interval to update timer
    const timerInterval = setInterval(() => {
      // Calculate elapsed time since this effect started
      const elapsedSinceStart = Date.now() - startTimeRef.current;
      
      // Calculate current remaining time
      const currentRemainingMs = Math.max(0, initialRemainingMs - elapsedSinceStart);
      
      // Update state
      setTimeRemaining(currentRemainingMs);
      
      // Check if time is running low (less than 5 minutes)
      setIsLowTime(currentRemainingMs > 0 && currentRemainingMs < 5 * 60 * 1000);
      
      // If timer reaches zero, clear interval and call onTimeUp callback
      if (currentRemainingMs <= 0) {
        clearInterval(timerInterval);
        if (onTimeUp) {
          onTimeUp();
        }
      }
    }, 1000);
    
    // Clean up interval on unmount or when dependencies change
    return () => clearInterval(timerInterval);
  }, [initialMinutes, startDate, timerDuration, onTimeUp]);

  // Format time remaining as HH:MM:SS if over 60 minutes, otherwise MM:SS
  const formatTimeRemaining = useCallback(() => {
    if (timeRemaining === null) return '--:--';
    
    const totalSeconds = Math.floor(timeRemaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    // If hours > 0, show HH:MM:SS format
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    
    // Otherwise show MM:SS format
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [timeRemaining]);

  return (
    <div className={`font-mono font-bold text-lg flex items-center ${isLowTime ? 'text-red-600 animate-pulse' : 'text-gray-700'}`}>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {formatTimeRemaining()}
    </div>
  );
}

export default CountdownTimer;
